// The Unsaid — conversation orchestration seam.
// UI is provider-agnostic: it calls this service, which calls the FioNova Web
// backend (/api/unsaid/next). The backend holds the adaptive AI controller and
// the LLM key server-side. Swapping the model/provider requires NO UI changes.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { authHeaders } from "@/lib/auth";
import { journeyHeaders } from "@/lib/journey";

const DISCOVERY_KEY = "fionova_discovery_id";
const discoveryHeaders = () => {
  try {
    if (sessionStorage.getItem("fionova_journey") === "1") return journeyHeaders();
    const id = sessionStorage.getItem(DISCOVERY_KEY); return id ? { "X-Discovery-Id": id } : {};
  } catch { return {}; }
};

// Server-issued free discovery session. Returns { ok } | { entitled } | { locked } | { limited }.
export async function ensureDiscoverySession() {
  try { if (sessionStorage.getItem(DISCOVERY_KEY)) return { ok: true }; } catch { /* storage unavailable */ }
  try {
    const res = await fetch(`${API}/discovery/session`, { method: "POST", headers: { ...authHeaders() } });
    if (res.status === 403) return { locked: true };
    if (res.status === 429) return { limited: true };
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    if (data.entitled) return { entitled: true };
    sessionStorage.setItem(DISCOVERY_KEY, data.discovery_id);
    return { ok: true };
  } catch (e) {
    return { ok: false };
  }
}

// Signed-in free account (no results yet) claims the finished anonymous draft.
export async function claimDiscovery(anonId) {
  const res = await fetch(`${API}/discovery/claim`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ anon_id: anonId }),
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()).user;
}

// Ongoing subscriber conversation with Ellie (server-verified entitlement + Companion Memory).
export async function ellieChat(messages) {
  const res = await fetch(`${API}/ellie/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ messages: (messages || []).map((m) => ({ role: m.role === "user" ? "user" : "ellie", text: m.text })) }),
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  const data = await res.json();
  return { message: data.message || "" };
}

// Persist the user's Companion Memory snapshot server-side (survives devices/logins).
export async function saveMemory(memory, onboardingCompleted) {
  try {
    await fetch(`${API}/me/memory`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ memory: memory || {}, onboarding_completed: onboardingCompleted }),
    });
  } catch {}
}

// Fire-and-forget: mark that the authenticated user has begun onboarding so their
// Fionova lifecycle status / Resend contact reflects "onboarding in progress".
export async function markOnboardingStarted() {
  try {
    if (!authHeaders().Authorization) return;
    await fetch(`${API}/me/onboarding-started`, { method: "POST", headers: { ...authHeaders() } });
  } catch {}
}


// Fixed, warm opener (same every session, by design) — the intelligence begins
// with the follow-ups, which are chosen adaptively by the backend controller.
const OPENING =
  "Before we go any further — tell me what's been taking up the most space in your head lately.";

const SOFT_FALLBACK =
  "Thank you for sharing that. Tell me a little more about what that's been like for you.";

export function getOpening() {
  return OPENING;
}

// Sends full conversation context every turn; the backend decides the next turn
// (or that it has enough) using the entire history.
export async function getNextTurn(history, { introUnsaid = false, discoveryMode = false, found = [] } = {}) {
  try {
    const res = await fetch(`${API}/unsaid/next`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
      body: JSON.stringify({
        history: history.map((m) => ({ role: m.role === "user" ? "user" : "guide", text: m.text })),
        intro_unsaid: introUnsaid,
        discovery_mode: discoveryMode,
        found,
      }),
    });
    if (res.status === 403) return { locked: true, done: true, prompt: "", crisis: false, discovery: null, features: [] };
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    return {
      done: !!data.done,
      prompt: data.prompt || "",
      crisis: !!data.crisis,
      discovery: data.discovery || null,
      features: Array.isArray(data.features) ? data.features : (data.discovery ? [data.discovery] : []),
    };
  } catch (e) {
    // Never surface technical errors to the user; keep the conversation graceful.
    return { done: false, prompt: SOFT_FALLBACK, crisis: false, discovery: null, features: [] };
  }
}

// Ellie's personalized post-X-Ray return message + discovery previews.
export async function generateJourney(history, observations, discoveries, xray) {
  try {
    const res = await fetch(`${API}/discovery/journey`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
      body: JSON.stringify({
        history: (history || []).map((m) => ({ role: m.role === "user" ? "user" : "guide", text: m.text })),
        observations: observations || [],
        discoveries: discoveries || [],
        xray: xray || {},
      }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    return data.ok ? { ok: true, journey: data.journey } : { ok: false, journey: null };
  } catch (e) {
    return { ok: false, journey: null };
  }
}

// Generates The Unsaid Reveal from the complete transcript. Returns ok=false on
// any failure so the UI can offer a graceful retry (never a fabricated reveal).
export async function generateReveal(history) {
  try {
    const res = await fetch(`${API}/unsaid/reveal`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
      body: JSON.stringify({
        history: history.map((m) => ({ role: m.role === "user" ? "user" : "guide", text: m.text })),
      }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    if (!data.ok || !Array.isArray(data.observations) || data.observations.length === 0) {
      return { ok: false, observations: [], handoff: {} };
    }
    return { ok: true, observations: data.observations, handoff: data.handoff || {} };
  } catch (e) {
    return { ok: false, observations: [], handoff: {} };
  }
}

// Generates the Emotional X-Ray from the existing Unsaid handoff. ok=false on
// failure so the UI can offer a graceful retry (never fabricated).
export async function generateXray(outcome) {
  try {
    const res = await fetch(`${API}/emotional-xray`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
      body: JSON.stringify({
        history: (outcome?.transcript || []).map((m) => ({ role: m.role === "user" ? "user" : "guide", text: m.text })),
        observations: outcome?.observations || [],
        feedback: outcome?.feedback || "",
        handoff: outcome?.handoff || {},
      }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    if (!data.ok || !data.xray) return { ok: false, xray: null };
    return { ok: true, xray: data.xray };
  } catch (e) {
    return { ok: false, xray: null };
  }
}

// Generates the Today Plan from the existing prior-phase handoffs. ok=false on
// failure so the UI can offer a graceful retry (never fabricated).
export async function generatePlan(unsaidOutcome, xray) {
  try {
    const res = await fetch(`${API}/today-plan`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
      body: JSON.stringify({
        history: (unsaidOutcome?.transcript || []).map((m) => ({ role: m.role === "user" ? "user" : "guide", text: m.text })),
        observations: unsaidOutcome?.observations || [],
        feedback: unsaidOutcome?.feedback || "",
        handoff: unsaidOutcome?.handoff || {},
        xray: xray || {},
      }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    if (!data.ok || !data.plan) return { ok: false, plan: null };
    return { ok: true, plan: data.plan };
  } catch (e) {
    return { ok: false, plan: null };
  }
}

// Adaptive Ellie-led Purpose Engine conversation. Returns ok=false on failure.
export async function purposeNext(context, history) {
  try {
    const res = await fetch(`${API}/purpose/next`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
      body: JSON.stringify({
        context: context || {},
        history: (history || []).map((m) => ({ role: m.role === "user" ? "user" : "ellie", text: m.text })),
      }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    return {
      ok: true,
      noticing: data.noticing || "",
      why_next: data.why_next || "",
      question: data.question || "",
      done: !!data.done,
      crisis: !!data.crisis,
    };
  } catch (e) {
    return { ok: false, noticing: "", why_next: "", question: "", done: false, crisis: false };
  }
}

// Generates the Purpose Reflection + Purpose Step from the whole journey. ok=false on failure.
export async function purposeReflection(context, history) {
  try {
    const res = await fetch(`${API}/purpose/reflection`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
      body: JSON.stringify({
        context: context || {},
        history: (history || []).map((m) => ({ role: m.role === "user" ? "user" : "ellie", text: m.text })),
      }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    if (!data.ok || !data.reflection) return { ok: false, reflection: null };
    return { ok: true, reflection: data.reflection };
  } catch (e) {
    return { ok: false, reflection: null };
  }
}

// Back-and-forth conversation with the user's future self.
// Returns ok=false on failure so the UI can offer a gentle retry (never a fake reply).
export async function futureChat(context, messages, closing = false) {
  try {
    const res = await fetch(`${API}/future-self/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
      body: JSON.stringify({
        context: context || {},
        messages: (messages || []).map((m) => ({ role: m.role === "user" ? "user" : "future", text: m.text })),
        closing: !!closing,
      }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    if (!data.message) throw new Error("empty message");
    return { ok: true, message: data.message };
  } catch (e) {
    return { ok: false, message: "" };
  }
}

// Selects at most one approved Josh Stickel book (or none) from accumulated context.
export async function bookRecommendation(context) {
  try {
    const res = await fetch(`${API}/book-recommendation`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
      body: JSON.stringify({ context: context || {} }),
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    return data.recommend ? { recommend: true, title: data.title, why: data.why, link: data.link || null } : { recommend: false };
  } catch (e) {
    return { recommend: false };
  }
}

// Starts a Stripe Checkout session for the Founders subscription (authenticated).
export async function startCheckout() {
  const res = await fetch(`${API}/payments/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ lookup_key: "founders_monthly", origin_url: window.location.origin }),
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json(); // { checkout_url, session_id }
}

// Starts Checkout with a 7-day free trial ($0 today, card required, first charge day 7).
export async function startTrialCheckout(trialDays = 7) {
  const res = await fetch(`${API}/payments/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ lookup_key: "founders_monthly", trial_days: trialDays, origin_url: window.location.origin }),
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json(); // { checkout_url, session_id }
}

// Server-decided offer + eligibility (the server also writes the exact billing terms). null on error.
export async function getTrialEligibility() {
  try {
    const res = await fetch(`${API}/payments/eligibility`, { headers: { ...authHeaders() } });
    if (!res.ok) throw new Error(`status ${res.status}`);
    return (await res.json()).offer || null;
  } catch (e) {
    return null;
  }
}

const postJson = async (path, body) => {
  const res = await fetch(`${API}${path}`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
    body: JSON.stringify(body || {}),
  });
  let data = {};
  try { data = await res.json(); } catch {}
  return { ok: res.ok, status: res.status, data };
};

export const startJourney = async () => {
  const res = await fetch(`${API}/journey/start`, { method: "POST", headers: { ...authHeaders(), ...journeyHeaders() } });
  let data = {};
  try { data = await res.json(); } catch {}
  return { ok: res.ok, status: res.status, data };
};
export const journeyRecap = (context, final) => postJson("/journey/recap", { context, final });
export const saveJourneyProgress = (step, data) => postJson("/journey/progress", { step, data });
export async function getJourneyProgress() {
  try {
    const res = await fetch(`${API}/journey/progress`, { headers: { ...authHeaders(), ...journeyHeaders() } });
    return res.ok ? await res.json() : { status: null };
  } catch { return { status: null }; }
}
export const checkInvite = (code) => postJson("/partner/check", { code });
export const redeemInvite = (code) => postJson("/partner/redeem", { code });
export const submitInquiry = (form) => postJson("/partnership/inquiry", form);
export async function adminInquiries() {
  const res = await fetch(`${API}/admin/inquiries`, { headers: { ...authHeaders() } });
  return { ok: res.ok, status: res.status, data: res.ok ? await res.json() : {} };
}
export async function setInquiryStatus(id, status) {
  const res = await fetch(`${API}/admin/inquiries/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ status }) });
  return res.ok;
}

// Stores the anonymous (pre-registration) discovery draft; returns { anon_id }.
export async function anonSaveXray(payload) {
  const res = await fetch(`${API}/anon/xray`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(), ...discoveryHeaders() },
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json();
}

// Fetches the authenticated user's stored memory (includes the Emotional X-Ray).
export async function getMemory() {
  const res = await fetch(`${API}/me/memory`, { headers: { ...authHeaders() } });
  if (!res.ok) throw new Error(`status ${res.status}`);
  const data = await res.json();
  return data.memory || data || {};
}

export async function getPaymentStatus(sessionId) {
  const res = await fetch(`${API}/payments/status/${sessionId}`, { headers: { ...authHeaders() } });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json(); // { session_id, status, payment_status, entitled }
}

// ---- Subscriber tools: Daily Journal, Goals, Progress, Journey History ----
const authJson = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json", ...authHeaders() },
  ...(body ? { body: JSON.stringify(body) } : {}),
});

export async function listJournal() {
  const res = await fetch(`${API}/me/journal`, { headers: { ...authHeaders() } });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()).entries || [];
}
export async function addJournal(text) {
  const res = await fetch(`${API}/me/journal`, authJson("POST", { text }));
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()).entry;
}

export async function listGoals() {
  const res = await fetch(`${API}/me/goals`, { headers: { ...authHeaders() } });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()).goals || [];
}
export async function addGoal(title, note) {
  const res = await fetch(`${API}/me/goals`, authJson("POST", { title, note }));
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()).goal;
}
export async function addGoalUpdate(id, text) {
  const res = await fetch(`${API}/me/goals/${id}/update`, authJson("POST", { text }));
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()).goal;
}
export async function completeGoal(id) {
  const res = await fetch(`${API}/me/goals/${id}/complete`, authJson("POST"));
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()).goal;
}

export async function listSnapshots(kind) {
  const res = await fetch(`${API}/me/snapshots?kind=${kind}`, { headers: { ...authHeaders() } });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()).snapshots || [];
}
export async function reflect(kind) {
  const res = await fetch(`${API}/me/reflect/${kind}`, authJson("POST"));
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()).snapshot;
}
// Fire-and-forget archival of an onboarding result (baseline snapshot).
export async function saveSnapshot(kind, data) {
  try { await fetch(`${API}/me/snapshot`, authJson("POST", { kind, data })); } catch {}
}

export async function getProgress() {
  const res = await fetch(`${API}/me/progress`, { headers: { ...authHeaders() } });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json();
}

// Opens the official Stripe Customer Portal for the authenticated user (returns to /app).
export async function openBillingPortal() {
  const res = await fetch(`${API}/payments/portal`, authJson("POST", { origin_url: window.location.origin }));
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json(); // { portal_url }
}

// Permanently deletes the authenticated user's account + personal data.
// Requires the current password and an explicit "DELETE" confirmation string.
export async function deleteAccount(password, confirm) {
  const res = await fetch(`${API}/account/delete`, authJson("POST", { password, confirm }));
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = typeof data.detail === "string" ? data.detail : "We couldn't delete your account. Please try again.";
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data; // { deleted: true }
}

// Clears all Fionova-held browser data (auth token + onboarding/journey content)
// after account deletion so nothing personal lingers on the device.
export function clearAllLocalData() {
  try {
    const keep = "fionova-theme"; // UI preference only — no personal data
    const ls = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k !== keep && (k.startsWith("fionova") || k.startsWith("fionova_"))) ls.push(k);
    }
    ls.forEach((k) => localStorage.removeItem(k));
  } catch {}
  try {
    const ss = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith("fionova")) ss.push(k);
    }
    ss.forEach((k) => sessionStorage.removeItem(k));
  } catch {}
}

// ----- Ongoing Ellie conversation (server-stored, per user) -----
const jsonOrThrow = async (res) => { if (!res.ok) throw new Error(`status ${res.status}`); return res.json(); };
export const getEllieHistory = async () => (await jsonOrThrow(await fetch(`${API}/ellie/history`, { headers: { ...authHeaders() } }))).messages || [];
export const sendEllie = async (text) => jsonOrThrow(await fetch(`${API}/ellie/chat`, authJson("POST", { text: text || null })));
export const clearEllieHistory = async () => jsonOrThrow(await fetch(`${API}/ellie/history`, { method: "DELETE", headers: { ...authHeaders() } }));
export const suggestionAction = async (messageId, action) => jsonOrThrow(await fetch(`${API}/ellie/suggestion`, authJson("POST", { message_id: messageId, action })));
export const getCompanion = async () => jsonOrThrow(await fetch(`${API}/me/companion`, { headers: { ...authHeaders() } }));
export const editNote = async (id, text, kind) => jsonOrThrow(await fetch(`${API}/me/companion/notes/${id}`, authJson("PATCH", { text, kind })));
export const deleteNote = async (id) => jsonOrThrow(await fetch(`${API}/me/companion/notes/${id}`, { method: "DELETE", headers: { ...authHeaders() } }));
export const setCompanionSettings = async (patch) => (await jsonOrThrow(await fetch(`${API}/me/companion/settings`, authJson("POST", patch)))).settings;
export const shareJournalEntry = async (id, shared) => jsonOrThrow(await fetch(`${API}/me/journal/${id}/share`, authJson("POST", { shared })));
